import React, { useEffect, useMemo, useState } from "react";

const MAP_BLUEPRINT = [
  "~~~~~~~~~~~~",
  "~GGGG##GGGG~",
  "~G..TT..MGG~",
  "~G##..MMGGG~",
  "~G.TT.GGWGG~",
  "~GGMM..GHGG~",
  "~G..##..WGG~",
  "~GGG....MGG~",
  "~GTT..GGGGG~",
  "~~~~~~~~~~~~",
];

const TILE_TYPES = {
  "~": { label: "Puddle", color: "#1f3a93" },
  "#": { label: "Rose hedge", color: "#274120" },
  ".": { label: "Trail", color: "#b98946" },
  "G": { label: "Moss", color: "#4f7c2c" },
  "T": { label: "Tea sprig", color: "#8cc76b", icon: "🍃" },
  "M": { label: "Mushrooms", color: "#c46b48", icon: "🍄" },
  "H": { label: "Camp", color: "#d8c095", icon: "⛺" },
  "W": { label: "Wademon", color: "#6a47b8", icon: "?" },
};

const PASSABLE = new Set([".", "G", "T", "M", "H", "W"]);
const START_POS = { row: 5, col: 8 };

const DIRECTIONS = {
  ArrowUp: { row: -1, col: 0, label: "up" },
  ArrowDown: { row: 1, col: 0, label: "down" },
  ArrowLeft: { row: 0, col: -1, label: "left" },
  ArrowRight: { row: 0, col: 1, label: "right" },
};

const WADEDEX = [
  { id: "sproutling", name: "Sproutling Wade", vibe: "steam", desc: "Likes to nap near teacups." },
  { id: "mistral", name: "Mistral Wade", vibe: "mist", desc: "Skips across puddles when invited." },
  { id: "embercap", name: "Embercap Wade", vibe: "mushroom", desc: "Keeps tea warm with ember spores." },
  { id: "stormbud", name: "Stormbud Wade", vibe: "thunder", desc: "Collects raindrops in its coat hood." },
];

function clamp(n, min, max) {
  return Math.max(min, Math.min(max, n));
}

export default function Wademon() {
  const [board, setBoard] = useState(() => MAP_BLUEPRINT.map((row) => row.split("")));
  const [player, setPlayer] = useState(START_POS);
  const [teaLeaves, setTeaLeaves] = useState(0);
  const [mushrooms, setMushrooms] = useState(0);
  const [infusions, setInfusions] = useState(0);
  const [party, setParty] = useState([]);
  const [log, setLog] = useState([
    "A little boy in a yellow raincoat steps into the Mossy Wade Garden."
  ]);

  const boardRows = board.length;
  const boardCols = board[0]?.length || 0;

  const boardStyle = useMemo(() => ({
    display: "grid",
    gridTemplateColumns: `repeat(${boardCols}, 34px)`,
    gridTemplateRows: `repeat(${boardRows}, 34px)`,
    gap: 0,
    padding: 16,
    background: "#222b38",
    border: "4px solid #0b1118",
    boxShadow: "0 0 0 3px #f8f8d4",
    imageRendering: "pixelated",
  }), [boardCols, boardRows]);

  useEffect(() => {
    function handleKey(e) {
      if (DIRECTIONS[e.key]) {
        e.preventDefault();
        attemptMove(e.key);
      }
    }

    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  });

  function pushLog(message) {
    setLog((prev) => {
      const next = [message, ...prev];
      return next.slice(0, 8);
    });
  }

  function updateTile(row, col, value) {
    setBoard((prev) => {
      const clone = prev.map((r) => [...r]);
      clone[row][col] = value;
      return clone;
    });
  }

  function attemptMove(key) {
    const dir = DIRECTIONS[key];
    if (!dir) return;

    setPlayer((prev) => {
      const nextRow = clamp(prev.row + dir.row, 0, boardRows - 1);
      const nextCol = clamp(prev.col + dir.col, 0, boardCols - 1);

      const tile = board[nextRow][nextCol];
      if (!PASSABLE.has(tile)) {
        pushLog("That puddle is too deep to wade through.");
        return prev;
      }

      resolveTile(tile, nextRow, nextCol);
      return { row: nextRow, col: nextCol };
    });
  }

  function resolveTile(tile, row, col) {
    if (tile === "T") {
      updateTile(row, col, ".");
      setTeaLeaves((t) => t + 1);
      pushLog("You harvested a Sweet Wade Tea sprig.");
    } else if (tile === "M") {
      updateTile(row, col, ".");
      setMushrooms((m) => m + 1);
      pushLog("You carefully collect a cluster of glowing mushrooms.");
    } else if (tile === "W") {
      if (infusions > 0) {
        const recruit = nextWademon();
        if (recruit) {
          updateTile(row, col, ".");
          setInfusions((i) => i - 1);
          setParty((prev) => [...prev, recruit]);
          pushLog(`${recruit.name} sips the tea and joins your puddle patrol!`);
        } else {
          pushLog("All nearby Wademon already follow you. Leave some tea for later.");
        }
      } else {
        pushLog("This shy Wademon wants a warm cup of tea before trusting you.");
      }
    } else if (tile === "H") {
      pushLog("Campfire crackles. You can brew tea here.");
    }
  }

  function nextWademon() {
    return WADEDEX.find((mon) => !party.some((p) => p.id === mon.id));
  }

  function brewTea() {
    if (teaLeaves < 1 || mushrooms < 2) {
      pushLog("You need at least 1 tea sprig and 2 mushrooms to brew.");
      return;
    }
    setTeaLeaves((t) => t - 1);
    setMushrooms((m) => m - 2);
    setInfusions((i) => i + 1);
    pushLog("You brew a Wade-Warming infusion. It smells like rain and honey.");
  }

  function resetGarden() {
    setBoard(MAP_BLUEPRINT.map((row) => row.split("")));
    setPlayer(START_POS);
    setTeaLeaves(0);
    setMushrooms(0);
    setInfusions(0);
    setParty([]);
    setLog(["A little boy in a yellow raincoat steps into the Mossy Wade Garden."]);
  }

  function renderTile(rowIdx, colIdx, cell) {
    const isPlayer = rowIdx === player.row && colIdx === player.col;
    const tile = TILE_TYPES[cell] || TILE_TYPES["."];
    const baseStyle = {
      width: 34,
      height: 34,
      border: "1px solid #0b1118",
      background: tile.color,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      position: "relative",
    };

    return (
      <div key={`${rowIdx}-${colIdx}`} style={baseStyle} title={tile.label}>
        {isPlayer ? (
          <div style={{
            width: 18,
            height: 24,
            background: "linear-gradient(#f8d93b 0 70%, #2b1b0b 70%)",
            border: "2px solid #2b1b0b",
            borderRadius: 4,
            boxShadow: "0 0 4px rgba(0,0,0,0.4)",
          }} />
        ) : (
          tile.icon && <span style={{ fontSize: 14 }}>{tile.icon}</span>
        )}
        {cell === "W" && !isPlayer && (
          <div style={{
            position: "absolute",
            inset: 4,
            border: "2px dashed rgba(255,255,255,0.4)",
            borderRadius: 4,
          }} />
        )}
      </div>
    );
  }

  return (
    <div style={{ fontFamily: "'Press Start 2P', monospace, 'VT323', sans-serif", color: "#f8f8d4", background: "#0a1018", minHeight: "100vh", padding: "32px 16px" }}>
      <div style={{ maxWidth: 960, margin: "0 auto" }}>
        <h1 style={{ fontSize: 28, marginBottom: 8 }}>Wademon: Mossy Wade Garden</h1>
        <p style={{ lineHeight: 1.5, maxWidth: 720 }}>
          Wander the rain-slick garden, gather tea sprigs and mushrooms, then brew Wade-Warming tea.
          Offer it to shy Wademon you meet on the path—just like a tiny, tea-scented Pokémon adventure.
        </p>

        <div style={{ display: "flex", flexWrap: "wrap", gap: 24, marginTop: 24 }}>
          <div style={{ flex: "1 1 320px" }}>
            <div style={boardStyle}>
              {board.map((row, rowIdx) =>
                row.map((cell, colIdx) => renderTile(rowIdx, colIdx, cell))
              )}
            </div>
          </div>

          <aside style={{
            flex: "1 1 260px",
            background: "#131c29",
            border: "2px solid #1f2a3a",
            padding: 16,
            borderRadius: 8,
          }}>
            <h2 style={{ fontSize: 18, marginBottom: 12 }}>Field Guide</h2>
            <ul style={{ listStyle: "square", paddingLeft: 18, lineHeight: 1.6, fontSize: 14 }}>
              <li>Use arrow keys or the D-pad to move.</li>
              <li>Collect <b>tea sprigs</b> (🍃) and <b>mushrooms</b> (🍄).</li>
              <li>Brew Wade-Warming tea (1 sprig + 2 mushrooms) at any time.</li>
              <li>Stand next to a Wademon tile and offer tea to befriend it.</li>
            </ul>
            <div style={{ marginTop: 20 }}>
              <h3 style={{ fontSize: 16, marginBottom: 8 }}>Satchel</h3>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 8 }}>
                <StatChip label="Tea" value={teaLeaves} color="#8cc76b" />
                <StatChip label="Mush" value={mushrooms} color="#c46b48" />
                <StatChip label="Infusions" value={infusions} color="#f2c94c" />
                <StatChip label="Party" value={party.length} color="#6a47b8" />
              </div>
              <button onClick={brewTea} style={buttonStyle} disabled={teaLeaves < 1 || mushrooms < 2}>
                Brew Wade Tea
              </button>
              <button onClick={resetGarden} style={{ ...buttonStyle, marginTop: 8, background: "#f16b6b" }}>
                Reset Garden
              </button>
            </div>
            <div style={{ marginTop: 24 }}>
              <h3 style={{ fontSize: 16, marginBottom: 8 }}>Party</h3>
              {party.length === 0 && <p style={{ fontSize: 13 }}>No Wademon yet. Brew tea to earn their trust.</p>}
              {party.map((mon) => (
                <div key={mon.id} style={{
                  border: "1px solid #2f3d57",
                  padding: "8px 10px",
                  borderRadius: 6,
                  marginBottom: 8,
                  background: "rgba(255,255,255,0.05)",
                }}>
                  <div style={{ fontSize: 14, fontWeight: "bold" }}>{mon.name}</div>
                  <div style={{ fontSize: 12, color: "#cdd2f0" }}>{mon.desc}</div>
                </div>
              ))}
            </div>
          </aside>
        </div>

        <div style={{ marginTop: 24, display: "flex", flexWrap: "wrap", gap: 24 }}>
          <div>
            <h3 style={{ fontSize: 16, marginBottom: 8 }}>D-Pad</h3>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 52px)", gridTemplateRows: "repeat(3, 52px)", gap: 6 }}>
              {["", "ArrowUp", "", "ArrowLeft", "", "ArrowRight", "", "ArrowDown", ""].map((key, idx) => (
                <button
                  key={`${key}-${idx}`}
                  onClick={() => key && attemptMove(key)}
                  disabled={!key}
                  style={{
                    width: 52,
                    height: 52,
                    borderRadius: 8,
                    border: "2px solid #2f3d57",
                    background: key ? "#1e2a3f" : "transparent",
                    color: "#f8f8d4",
                    cursor: key ? "pointer" : "default",
                    fontSize: 12,
                  }}
                >
                  {key.replace("Arrow", "")}
                </button>
              ))}
            </div>
          </div>
          <div style={{ flex: "1 1 320px" }}>
            <h3 style={{ fontSize: 16, marginBottom: 8 }}>Adventure Log</h3>
            <div style={{
              minHeight: 160,
              maxHeight: 220,
              overflowY: "auto",
              padding: 12,
              background: "#0f1724",
              border: "2px inset #253045",
              fontSize: 13,
              lineHeight: 1.5,
            }}>
              {log.map((entry, idx) => (
                <div key={idx} style={{ marginBottom: 6 }}>
                  {entry}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function StatChip({ label, value, color }) {
  return (
    <div style={{
      border: `2px solid ${color}`,
      borderRadius: 8,
      padding: "8px 10px",
      textAlign: "center",
      fontSize: 13,
      fontWeight: 600,
      background: "rgba(0,0,0,0.3)",
    }}>
      <div style={{ fontSize: 11, opacity: 0.8 }}>{label}</div>
      <div style={{ fontSize: 16 }}>{value}</div>
    </div>
  );
}

const buttonStyle = {
  width: "100%",
  marginTop: 12,
  padding: "10px 12px",
  borderRadius: 8,
  background: "#3dd598",
  color: "#08211c",
  border: "none",
  fontWeight: 700,
  cursor: "pointer",
  fontSize: 14,
};
